import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { auth } from '@/lib/api';
import { getStoredUser, setStoredUser, clearStoredUser } from '@/lib/auth';
import { Loader2 } from 'lucide-react';

export default function ProtectedRoute({ children, requiredRole }) {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [user, setUser] = useState(location.state?.user || null);
  
  useEffect(() => {
    // If user data was passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setUser(location.state.user);
      setStoredUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }
    
    const checkAuth = async () => {
      // First check localStorage
      const storedUser = getStoredUser();
      if (storedUser) {
        setUser(storedUser);
        setIsAuthenticated(true);
        return;
      }
      
      // Then verify with server
      try {
        const response = await auth.me();
        setUser(response.data);
        setStoredUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        clearStoredUser();
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
  }, [location.state]);
  
  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Wrong role
  if (requiredRole && user?.role !== requiredRole) {
    let redirectPath = '/toc';
    if (user?.role === 'employer') redirectPath = '/employer';
    else if (user?.role === 'subscription_manager') redirectPath = '/admin';
    return <Navigate to={redirectPath} replace />;
  }
  
  return children;
}
